import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getSeasons } from '../seasons-assets.js';
import { FREEZE_TIME_S } from '../debug-capture.js';

// Contrat du rig partagé : interdit. Deux maillages en espace écran.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'seasons',
  family: 'photographic',
  danceability: 0.14,
  energyLevel: 0.08,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.08,
  tonality: 0.95,
  enabled: true
};

// Seasons : un arbre seul au-dessus d'un lac traverse l'année, à partir de
// quatre photos du même paysage (printemps, été, automne, hiver) alignées au
// build (tools/build-seasons.mjs et tools/seasons-build.py, qui documentent
// l'alignement mesuré, les masques et les cartes). Piano, folk, musique de
// film, chanson douce. Règles R1-R20 de kiosk-app/CLAUDE.md ; dansabilité
// 0,14 : aucune impulsion visible (R9), la musique ne fait que le vent.
//
// L'ANNÉE (R20, lib/scene-clock.js) : 40 s, saison tenue 6 s, transition 4 s,
//   printemps 0-6 | p->é 6-10 | été 10-16 | é->a 16-20 | automne 20-26 |
//   a->h 26-30 | hiver 30-36 | h->p 36-40, et la boucle se referme.
// Départ au hasard, jamais dans l'hiver tenu. Toute fenêtre de 20 s contient
// au moins une transition entière et deux saisons.
// Avec l'été et l'hiver seuls (repli) : été 16 s, é->h 4 s (carte 2), hiver
// 16 s, h->é 4 s (carte 3).
//
// UNE TRANSITION n'est jamais un fondu uniforme : chaque pixel a son instant
// T(x) (carte 16 bits du build) et
//     couleur = mix(A, B, smoothstep(T - 0.08, T + 0.08, t)),
// t allant de -0.08 à 1.08 pour que tout pixel finisse. Les cartes portent le
// récit : feuilles par touches, couronne qui roussit par le bord, collines
// lointaines d'abord, neige sur les surfaces exposées et lointaines, lac qui
// gèle depuis ses rives, fonte d'abord en bas et au clair ; au retour du
// printemps des touches de fleurs blanches et roses (ce shader) précèdent la
// photo.
//
// LA COUCHE VIVANTE (un seul maillage instancié, tout le mouvement dans le
// vertex shader, aucun envoi par frame) : pétales au printemps, graines et
// lucioles l'été, feuilles d'automne qui partent de la couronne AU MOMENT où
// la couronne s'y dégarnit (leur T de la carte 2) et portent la couleur de
// la photo d'automne à leur point de départ (manifest.leaves), neige de 1 à
// 3 px (plus grosse et floue au premier plan), une volée d'oiseaux par année.
// Dans le shader photo : nuages qui dérivent dans le ciel, brume de vallée
// à l'automne, brume froide l'hiver, ride du lac (gelé : immobile), vent de
// 1 à 2 px dans la couronne et l'herbe, toujours pondéré par les masques
// adoucis (R8).
//
// LA MUSIQUE : adapt.intensity lissée sur 8 mesures = le vent (amplitude du
// balancement, ride, nombre de pétales, de feuilles, de flocons).
//
// CADRAGE (R18, R19) : la photo couvre l'écran (échelle max), l'arbre au
// tiers droit ; sur un écran plus étroit que 16:9 le recadrage tire vers
// l'arbre (manifest.anchor). Marge de 0,6 % pour le vent : aucun bord.
// CONFORT (R10, calme) : gain tel que la luminance linéaire moyenne de la
// photo du moment reste sous 0,25 (manifest.meanLin).
//
// PRÉCISION : le shader photo est en highp (exception documentée) : T se
// reconstruit sur 16 bits (mediump n'en garde que 11) et les cellules des
// fleurs se comptent en pixels d'image jusqu'à 1376. Les particules : highp
// au vertex (positions), mediump au fragment.
//
// Debug : ?seasonst=<f> fige l'année (phase 0-1) ; ?seasonsmask=1 montre les
// masques (R couronne, G bois, B ciel ; cyan le lac) ; ?seasonsmap=<k> montre
// la carte de transition k (0 p->é, 1 é->a, 2 a->h, 3 h->p).

const YEAR_S = 40;
const TIME_WRAP = 120;          // every particle period divides it
const SAFE_FLOOR = 0.5;
const MARGIN = 0.006;           // crop margin for the wind's displacement
const TIER = {
  0: { leaves: 290, petals: 150, motes: 70, snow: 280 },
  1: { leaves: 500, petals: 260, motes: 130, snow: 500 },
  2: { leaves: 700, petals: 380, motes: 180, snow: 720 }
};
const BIRDS = 9;
const NAMES = ['spring', 'summer', 'autumn', 'winter'];
// the year's script: holds and transitions (map index k, from, to)
const FOUR = [
  { s: 'spring', d: 6 }, { k: 0, a: 'spring', b: 'summer', d: 4 },
  { s: 'summer', d: 6 }, { k: 1, a: 'summer', b: 'autumn', d: 4 },
  { s: 'autumn', d: 6 }, { k: 2, a: 'autumn', b: 'winter', d: 4 },
  { s: 'winter', d: 6 }, { k: 3, a: 'winter', b: 'spring', d: 4 }
];
const TWO = [
  { s: 'summer', d: 16 }, { k: 2, a: 'summer', b: 'winter', d: 4 },
  { s: 'winter', d: 16 }, { k: 3, a: 'winter', b: 'summer', d: 4 }
];
// the grade: a colour multiplier per season (exposure x temperature)
const GRADE = {
  spring: [1.00, 1.00, 1.00],
  summer: [1.03, 1.00, 0.94],
  autumn: [1.06, 0.97, 0.86],
  winter: [0.93, 0.98, 1.08]
};

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// where the year is: the segment, its progress, the season weights
export function yearAt(script, t) {
  let t0 = 0;
  for (const seg of script) {
    if (t < t0 + seg.d || seg === script[script.length - 1]) {
      const p = clamp01((t - t0) / seg.d);
      const w = { spring: 0, summer: 0, autumn: 0, winter: 0 };
      if (seg.s) { w[seg.s] = 1; return { seg, p, t0, a: seg.s, b: seg.s, k: -1, w }; }
      const q = smooth(0, 1, p);
      w[seg.a] += 1 - q; w[seg.b] += q;
      return { seg, p, t0, a: seg.a, b: seg.b, k: seg.k, w };
    }
    t0 += seg.d;
  }
  return null;
}
function segStart(script, test) {
  let t0 = 0;
  for (const seg of script) { if (test(seg)) return t0; t0 += seg.d; }
  return -1;
}

// a tileable value-noise fbm, two octave sets in R and G (clouds, mist)
function noiseTexture() {
  const N = 128, data = new Uint8Array(N * N * 4), rnd = mulberry(7071);
  const grid = (g) => { const a = new Float32Array(g * g); for (let i = 0; i < a.length; i++) a[i] = rnd(); return a; };
  const sample = (a, g, x, y) => {
    const fx = x * g / N, fy = y * g / N, x0 = Math.floor(fx), y0 = Math.floor(fy), tx = fx - x0, ty = fy - y0;
    const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
    const v = (i, j) => a[((j % g + g) % g) * g + ((i % g + g) % g)];
    return (v(x0, y0) * (1 - sx) + v(x0 + 1, y0) * sx) * (1 - sy) + (v(x0, y0 + 1) * (1 - sx) + v(x0 + 1, y0 + 1) * sx) * sy;
  };
  const oct = [[4, grid(4)], [8, grid(8)], [16, grid(16)], [32, grid(32)]];
  const oct2 = [[4, grid(4)], [8, grid(8)], [16, grid(16)]];
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    let r = 0, g = 0, amp = 1, tot = 0;
    for (const [n, a] of oct) { r += amp * sample(a, n, x, y); tot += amp; amp *= 0.5; }
    r /= tot; amp = 1; tot = 0;
    for (const [n, a] of oct2) { g += amp * sample(a, n, x, y); tot += amp; amp *= 0.5; }
    g /= tot;
    const o = (y * N + x) * 4;
    data[o] = r * 255; data[o + 1] = g * 255; data[o + 2] = 0; data[o + 3] = 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const PHOTO_FRAGMENT = `
  precision highp float;
  uniform sampler2D uA;
  uniform sampler2D uB;
  uniform sampler2D uT;
  uniform sampler2D uMask;
  uniform sampler2D uMask2;
  uniform sampler2D uNoise;
  uniform vec4 uMap;             // image uv of the screen's top-left, image uv span
  uniform vec2 uImg;             // image size, px
  uniform float uTT;             // the transition's t, -0.08 .. 1.08 (holds: 2.0)
  uniform float uFlowers;        // 1 during winter -> spring
  uniform vec2 uIce;             // A is winter, B is winter
  uniform float uTime;
  uniform float uWind;           // 0..1, smoothed over 8 bars
  uniform vec4 uSeason;          // weights spring, summer, autumn, winter
  uniform vec3 uGrade;
  uniform float uGain;
  uniform float uDebug;          // 1 masks, 2 map
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  vec2 tex(vec2 img) { return vec2(img.x, 1.0 - img.y); }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

  void main() {
    vec2 img = uMap.xy + vec2(vUv.x, 1.0 - vUv.y) * uMap.zw;
    vec4 m = texture2D(uMask, tex(img));
    vec4 m2 = texture2D(uMask2, tex(img));
    float crown = m.r, wood = m.g, sky = m.b, lake = m2.r, prox = m2.g;
    float ground = clamp(1.0 - crown - wood - sky - lake, 0.0, 1.0);
    vec4 tm = texture2D(uT, tex(img));
    float T = (tm.r * 65280.0 + tm.g * 255.0) / 65535.0;
    float w = uTT > 1.5 ? 0.0 : smoothstep(T - 0.08, T + 0.08, uTT);
    float ice = mix(uIce.x, uIce.y, w);

    // the wind: 1 to 2 px, weighted by the SOFT masks (R8), more at the
    // crown's top; slow travelling waves, never on the beat
    vec2 px = 1.0 / uImg;
    float amp = 0.7 + 1.3 * uWind;
    float hi = clamp(1.0 - img.y / 0.65, 0.0, 1.0);
    vec2 sway = vec2(sin(uTime * 0.9 + img.x * 9.0 + img.y * 5.0) + 0.5 * sin(uTime * 2.1 + img.y * 31.0),
                     0.35 * sin(uTime * 1.3 + img.x * 17.0));
    vec2 grass = vec2(sin(uTime * 1.6 - img.x * 40.0 + img.y * 12.0), 0.0);
    vec2 d = (sway * crown * (0.4 + 0.6 * hi) + grass * ground * smoothstep(0.62, 0.9, img.y) * 0.8) * amp * px;
    // the lake: a slow ripple in its reflection, still once frozen
    d.y += lake * (1.0 - ice) * sin(uTime * 1.7 + img.y * 900.0) * (0.6 + 0.9 * uWind) * px.y;
    d.x += lake * (1.0 - ice) * sin(uTime * 1.1 + img.y * 420.0) * 0.6 * px.x;
    vec3 a = texture2D(uA, tex(img + d)).rgb;
    vec3 b = texture2D(uB, tex(img + d)).rgb;
    vec3 c = mix(a, b, w);

    // the blossom's touches ahead of the spring photo (winter -> spring)
    if (uFlowers > 0.5) {
      vec2 ip = img * uImg / 4.0;
      vec2 cell = floor(ip);
      float h = hash(cell);
      vec2 o = vec2(hash(cell + 13.1), hash(cell + 71.7)) * 0.6 + 0.2;
      float r = length((fract(ip) - o) * 4.0);             // image px
      float spot = 1.0 - smoothstep(0.55, 1.35, r);
      float where = max(crown, ground * 0.6) * step(0.55, h);
      float f = smoothstep(T - 0.26, T - 0.12, uTT) * (1.0 - w);
      vec3 col = mix(vec3(1.0, 0.97, 0.95), vec3(1.0, 0.70, 0.80), step(0.8, h));
      c = mix(c, col * 0.85, spot * where * f);
    }

    // clouds drifting across the sky (spring, summer), mists (autumn
    // valley, winter cold), from a tileable noise
    vec2 nuv = img * vec2(uImg.x / uImg.y, 1.0);
    float cl = texture2D(uNoise, nuv * 0.9 + vec2(uTime * 0.004, 0.0)).r;
    float cloud = smoothstep(0.52, 0.8, cl) * sky * (0.10 * uSeason.x + 0.16 * uSeason.y);
    c = mix(c, vec3(0.98, 0.98, 1.0), cloud);
    float mist = texture2D(uNoise, nuv * vec2(1.4, 3.0) + vec2(uTime * 0.006, 0.0)).g;
    // only far away (low proximity), never over the tree
    float band = smoothstep(0.50, 0.58, img.y) * (1.0 - smoothstep(0.66, 0.80, img.y))
               * (1.0 - smoothstep(0.06, 0.28, prox)) * clamp(1.0 - sky - crown - wood, 0.0, 1.0);
    float mA = band * smoothstep(0.35, 0.75, mist) * (0.30 * uSeason.z + 0.22 * uSeason.w);
    c = mix(c, mix(vec3(0.95, 0.92, 0.86), vec3(0.85, 0.90, 1.0), uSeason.w), mA);

    c *= uGrade * uGain;
    if (uDebug > 1.5) c = vec3(T);
    else if (uDebug > 0.5) c = mix(c * 0.4, vec3(crown, wood, sky) + vec3(0.0, lake, lake), 0.7);

    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    c *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(c, 1.0);
  }
`;

// kinds: 0 petal, 1 mote (seed or firefly), 2 leaf, 3 snow, 4 bird
const PARTICLE_VERTEX = `
  precision highp float;
  attribute vec4 aKind;          // kind, size px, phase, life s
  attribute vec4 aSeed;
  attribute vec4 aPos;           // leaf/petal: anchor uv, T, -
  attribute vec3 aCol;
  uniform vec4 uMap;
  uniform vec2 uView;            // viewport px
  uniform float uTime;           // wraps at ${TIME_WRAP} s
  uniform float uCycleT;         // seconds in the year
  uniform float uWind;
  uniform vec4 uDens;            // petals, motes, snow, leaves
  uniform vec3 uLeafT;           // transition start, its length, the hold before it
  uniform vec4 uBird;            // start s in the year, direction, y, on
  varying vec2 vQ;
  varying vec4 vInfo;            // kind, alpha, shape param, size px
  varying vec3 vCol;

  float h1(float n) { return fract(sin(n * 12.9898) * 43758.5453); }
  vec2 toClip(vec2 img) {
    vec2 s = (img - uMap.xy) / uMap.zw;
    return vec2(s.x * 2.0 - 1.0, 1.0 - s.y * 2.0);
  }

  void main() {
    float kind = aKind.x, size = aKind.y;
    vec2 img = vec2(-9.0);
    float alpha = 0.0, rot = 0.0, squash = 1.0;
    vCol = aCol;
    if (kind < 1.5 || (kind > 2.5 && kind < 3.5)) {
      // periodic kinds: a new start each life (index kept modulo the wrap)
      float P = aKind.w;
      float age = mod(uTime + aKind.z * P, P);
      float life = mod(floor((uTime + aKind.z * P) / P), ${TIME_WRAP}.0 / P);
      float r1 = h1(life * 7.31 + aSeed.x * 91.7), r2 = h1(life * 3.17 + aSeed.y * 53.1);
      float k = age / P;
      float dens = kind < 0.5 ? uDens.x : (kind < 1.5 ? uDens.y : uDens.z);
      float on = smoothstep(aSeed.z, aSeed.z + 0.08, dens);
      if (kind < 0.5) {
        // a petal: off the crown, carried by the wind, slowly down
        img = aPos.xy + vec2((0.05 + 0.10 * uWind) * age + 0.015 * sin(age * 1.3 + r1 * 6.3), 0.035 * age + 0.01 * sin(age * 2.1 + r2 * 6.3));
        img.x += (r1 - 0.5) * 0.04;
        alpha = on * smoothstep(0.0, 0.08, k) * (1.0 - smoothstep(0.75, 1.0, k)) * 0.9;
        rot = age * (1.5 + 2.0 * r2); squash = 0.45 + 0.55 * abs(cos(age * 2.3 + r1 * 6.3));
      } else if (kind < 1.5) {
        // a mote: aSeed.w < 0.5 a seed drifting through, else a firefly low over the grass
        if (aSeed.w < 0.5) {
          img = vec2(r1 - 0.05 + (0.04 + 0.06 * uWind) * age, 0.30 + 0.45 * r2 + 0.02 * sin(age * 0.9 + r1 * 6.3));
        } else {
          img = vec2(r1, 0.66 + 0.30 * r2) + 0.025 * vec2(sin(age * 0.5 + r2 * 6.3), sin(age * 0.37 + r1 * 6.3));
        }
        alpha = on * sin(3.14159 * k) * (0.6 + 0.4 * sin(age * 1.1 + r2 * 6.3));
      } else {
        // a flake: screen-wide, near ones bigger, blurred, faster
        float near = step(0.85, aSeed.w);
        img = uMap.xy + vec2(r1 * 1.1 - 0.05, -0.05 + k * 1.12) * uMap.zw;
        img.x += ((0.03 + 0.06 * uWind) * age + 0.012 * sin(age * 1.4 + r2 * 6.3)) * uMap.z;
        alpha = on * smoothstep(0.0, 0.05, k) * (1.0 - smoothstep(0.92, 1.0, k)) * mix(0.75, 0.55, near);
      }
    } else if (kind < 2.5) {
      // a leaf: leaves the crown when the crown thins there (its T in the
      // autumn -> winter map), or earlier in the autumn for a quarter
      float launch = aSeed.w < 0.25 && uLeafT.z > 0.0
        ? uLeafT.x - uLeafT.z + aSeed.x * uLeafT.z
        : uLeafT.x + aPos.z * uLeafT.y - 0.2;
      float age = uCycleT - launch;
      // down to the ground under the tree, 2 to 7 s
      float fallY = max(aPos.y + 0.1, 0.72 + 0.2 * aSeed.y);
      float speed = 0.10 + 0.05 * aSeed.z;
      float tLand = (fallY - aPos.y) / speed;
      float ta = clamp(age, 0.0, tLand);
      img = aPos.xy + vec2((0.02 + 0.07 * uWind) * ta + 0.02 * sin(ta * 1.6 + aSeed.x * 6.3), speed * ta);
      float on = smoothstep(aSeed.z * 0.6, aSeed.z * 0.6 + 0.1, uDens.w);
      alpha = age > 0.0 ? on * smoothstep(0.0, 0.3, age) * (1.0 - smoothstep(tLand + 0.3, tLand + 1.0, age)) : 0.0;
      rot = ta * (1.2 + 2.5 * aSeed.y) * (aSeed.x < 0.5 ? -1.0 : 1.0);
      squash = 0.25 + 0.75 * abs(cos(ta * (2.0 + 2.0 * aSeed.z) + aSeed.y * 6.3));
      vCol = aCol * (age > tLand ? 0.85 : mix(0.7, 1.0, squash));
    } else {
      // a bird of the year's one flight, in a loose V
      float age = uCycleT - uBird.x;
      float i = aSeed.x;                             // rank in the V, signed
      vec2 v = vec2(uBird.y > 0.0 ? -0.08 : 1.08, uBird.z) + vec2(uBird.y * 0.13 * age, 0.0)
          + vec2(-uBird.y * abs(i) * 0.022, i * 0.016) + vec2(0.0, 0.004 * sin(age * 0.8 + i * 2.0));
      img = uMap.xy + v * uMap.zw;
      alpha = uBird.w * step(0.0, age) * step(age, 10.0);
      rot = age * (5.5 + aSeed.y) + aSeed.z * 6.3;   // the flap's phase
    }
    vInfo = vec4(kind, alpha, kind > 3.5 ? sin(rot) : squash, size);
    vQ = position.xy;
    if (alpha < 0.002) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    // the quad: a rotated square of +-size px (birds keep level)
    vec2 c = position.xy;
    float cr = cos(rot), sr = sin(rot);
    vec2 o = kind > 3.5 ? c : vec2(c.x * cr - c.y * sr, c.x * sr + c.y * cr);
    vec2 clip = toClip(img) + o * size * 2.0 / uView;
    gl_Position = vec4(clip, 0.0, 1.0);
  }
`;

const PARTICLE_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  uniform vec3 uGrade;
  uniform float uAspect;
  uniform vec2 uView;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vQ;
  varying vec4 vInfo;
  varying vec3 vCol;
  void main() {
    float kind = vInfo.x, size = vInfo.w;
    vec2 p = vQ * size;                               // px from the centre
    float a = 0.0;
    vec3 col = vCol;
    if (kind < 0.5) {
      // petal: a soft ellipse ~1.5 x 1 px, squashed by its tumble
      vec2 e = p / vec2(1.6 * max(vInfo.z, 0.35), 1.1);
      a = 1.0 - smoothstep(0.6, 1.25, length(e));
    } else if (kind < 1.5) {
      // mote: a 1 px core and a halo that is 0 at the quad's edge (R12)
      float r = length(p);
      a = (1.0 - smoothstep(0.6, 1.4, r)) + 0.35 * exp(-r / 1.6) * (1.0 - smoothstep(size * 0.6, size, r));
    } else if (kind < 2.5) {
      // leaf: pointed ellipse, 6 x 3 px, its width the flutter
      vec2 e = p / vec2(3.1, 1.5 * max(vInfo.z, 0.2));
      float tip = 1.0 - 0.35 * abs(e.x);
      a = 1.0 - smoothstep(0.75, 1.1, length(vec2(e.x, e.y / max(tip, 0.3))));
    } else if (kind < 3.5) {
      // flake: radius = size/3; the large near ones blurred
      float rad = size / 3.0;
      float soft = rad > 1.8 ? rad * 0.7 : 0.6;
      a = 1.0 - smoothstep(rad - soft, rad + soft, length(p));
    } else {
      // bird: a V of two 1.1 px strokes, wings up and down with the flap
      vec2 q = vec2(abs(p.x), p.y);
      float lift = 0.15 + 0.55 * vInfo.z;
      vec2 tip = vec2(size * 0.72, size * 0.72 * lift);
      float h = clamp(dot(q, tip) / dot(tip, tip), 0.0, 1.0);
      float dd = length(q - tip * h);
      a = 1.0 - smoothstep(0.4, 1.2, dd);
    }
    a *= vInfo.y;
    if (a < 0.004) discard;
    vec2 s = vec2((gl_FragCoord.x / uView.x * 2.0 - 1.0) * uAspect, gl_FragCoord.y / uView.y * 2.0 - 1.0);
    vec2 q2 = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q2, 0.0)) + min(max(q2.x, q2.y), 0.0);
    float safe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col * uGrade * uGain * safe * a, a);
  }
`;

export class SeasonsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const f = parseFloat(urlParam('seasonst'));
    this._forced = Number.isFinite(f) ? ((f % 1) + 1) % 1 : null;
    const mapK = parseInt(urlParam('seasonsmap'), 10);
    this._debugMap = mapK >= 0 && mapK <= 3 ? mapK : -1;
    this._debug = urlParam('seasonsmask') === '1' ? 1 : (this._debugMap >= 0 ? 2 : 0);
    // a calm, natural image: no RGB split, a whisper of bloom on the snow
    this._post = { bloomStrength: 0.10, bloomThreshold: 0.9, grain: 0.01, vignette: 0.28, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  init(scene) {
    this._scene = scene;
    const A = getSeasons();
    if (!A) { this._unavailable = true; return; }       // without its photos: unavailable
    this._A = A;
    const M = A.manifest;
    this._script = A.seasons.length >= 4 ? FOUR : TWO;
    this._adapt = new Adapt();
    // the year starts anywhere but in the held winter
    const rnd = mulberry(FREEZE_TIME_S != null ? 1234 : (Math.random() * 4294967296) >>> 0);
    const w0 = segStart(this._script, (s) => s.s === 'winter') / YEAR_S;
    const w1 = w0 + this._script.find((s) => s.s === 'winter').d / YEAR_S;
    let start = rnd() * (1 - (w1 - w0));
    if (start >= w0) start += w1 - w0;
    if (FREEZE_TIME_S != null) start = 0.37;            // reloaded captures: the same summer moment
    this._clock = new SceneClock({ period: YEAR_S, start: start % 1 });

    this._noise = noiseTexture();
    const u = this._u = {
      uA: { value: A.photos.summer }, uB: { value: A.photos.summer }, uT: { value: A.trans[0] },
      uMask: { value: A.masks }, uMask2: { value: A.masks2 }, uNoise: { value: this._noise },
      uMap: { value: new THREE.Vector4(0, 0, 1, 1) }, uImg: { value: new THREE.Vector2(M.size[0], M.size[1]) },
      uTT: { value: 2 }, uFlowers: { value: 0 }, uIce: { value: new THREE.Vector2() },
      uTime: { value: 0 }, uWind: { value: 0.3 }, uSeason: { value: new THREE.Vector4(0, 1, 0, 0) },
      uGrade: { value: new THREE.Vector3(1, 1, 1) }, uGain: { value: 0.7 }, uDebug: { value: this._debug },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather,
      uView: { value: new THREE.Vector2(1920, 1080) }, uCycleT: { value: 0 },
      uDens: { value: new THREE.Vector4() }, uLeafT: { value: new THREE.Vector3(26, 4, 6) },
      uBird: { value: new THREE.Vector4(-100, 1, 0.2, 0) }
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: u, vertexShader: QUAD_VERTEX, fragmentShader: PHOTO_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -1000;
    const self = this;
    this._quad.onBeforeRender = function (renderer) { self._frame(renderer); };
    scene.add(this._quad);

    // the living layer
    const L = this._layout;
    const leaves = A.leaves || [];
    const n = L.petals + L.motes + L.snow + (leaves.length ? L.leaves : 0) + BIRDS;
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0], 3));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const kind = new Float32Array(n * 4), seed = new Float32Array(n * 4), pos = new Float32Array(n * 4), col = new Float32Array(n * 3);
    const r = mulberry(20261016);
    let i = 0;
    const crownPick = () => leaves.length ? leaves[Math.floor(r() * leaves.length)] : [0.76, 0.3, 0, 1, 1, 1];
    for (let k = 0; k < L.petals; k++, i++) {
      const a = crownPick();
      kind.set([0, 3.2, r(), [8, 10, 12][k % 3]], i * 4); seed.set([r(), r(), r() * 0.92, r()], i * 4);
      pos.set([a[0], a[1], 0, 0], i * 4);
      col.set(r() < 0.35 ? [1.0, 0.72, 0.82] : [1.0, 0.97, 0.95], i * 3);
    }
    for (let k = 0; k < L.motes; k++, i++) {
      const s = r();
      kind.set([1, 6, r(), [20, 24][k % 2]], i * 4); seed.set([r(), r(), r() * 0.92, s], i * 4);
      col.set(s < 0.5 ? [0.95, 0.95, 0.90] : [1.0, 0.85, 0.45], i * 3);
    }
    for (let k = 0; k < L.snow; k++, i++) {
      const near = r();
      const sz = near > 0.85 ? 6 + 3 * r() : 1.6 + 2.8 * r();      // radius = size/3: 0.5-1.5 px, near 2-3 px
      kind.set([3, sz, r(), near > 0.85 ? 6 : [10, 12][k % 2]], i * 4); seed.set([r(), r(), r() * 0.92, near], i * 4);
      col.set([0.96, 0.97, 1.0], i * 3);
    }
    if (leaves.length) {
      // the leaves, evenly through the anchors (they come shuffled)
      for (let k = 0; k < L.leaves; k++, i++) {
        const a = leaves[Math.floor(k * leaves.length / L.leaves)];
        kind.set([2, 5, 0, 0], i * 4); seed.set([r(), r(), r(), r()], i * 4);
        pos.set([a[0], a[1], a[2], 0], i * 4);
        col.set([a[3], a[4], a[5]].map((v) => Math.pow(v, 2.2)), i * 3);   // the autumn photo's colour, linear
      }
    }
    for (let k = 0; k < BIRDS; k++, i++) {
      const rank = k === 0 ? 0 : (k % 2 ? 1 : -1) * Math.ceil(k / 2);
      kind.set([4, 5 + r() * 1.5, 0, 0], i * 4); seed.set([rank, r(), r(), 0], i * 4);
      col.set([0.03, 0.035, 0.045], i * 3);
    }
    g.setAttribute('aKind', new THREE.InstancedBufferAttribute(kind, 4));
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4));
    g.setAttribute('aPos', new THREE.InstancedBufferAttribute(pos, 4));
    g.setAttribute('aCol', new THREE.InstancedBufferAttribute(col, 3));
    g.instanceCount = n;
    this._count = n;
    this._parts = new THREE.Mesh(g, new THREE.ShaderMaterial({
      uniforms: u, vertexShader: PARTICLE_VERTEX, fragmentShader: PARTICLE_FRAGMENT, depthTest: false, depthWrite: false,
      transparent: true, blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor
    }));
    this._parts.frustumCulled = false; this._parts.renderOrder = -999;
    if (this._debug) this._parts.visible = false;
    scene.add(this._parts);

    this._time = 0; this._wind = 0.3; this._cycleSeen = -1;
    this._updateMap();
  }

  _updateMap() {
    // cover (R18): scale = max; on a narrower screen the crop pulls toward
    // the tree; a 0.6 % margin keeps the wind's pixels inside the photo
    const A = safeZoneUniforms.uAspect.value || 16 / 9;
    const [W, H] = this._A.manifest.size, Ai = W / H;
    let sx = 1, sy = 1;
    if (A > Ai) sy = Ai / A; else sx = A / Ai;
    sx *= 1 - 2 * MARGIN; sy *= 1 - 2 * MARGIN;
    const an = this._A.manifest.anchor || [W * 0.76, H * 0.4];
    const place = (a, s) => {
      const t = 0.5 + (a - 0.5) * s;                 // where the anchor lands on screen
      return Math.min(1 - s - MARGIN, Math.max(MARGIN, a - t * s));
    };
    this._u.uMap.value.set(place(an[0] / W, sx), place(an[1] / H, sy), sx, sy);
    this._mapAspect = A;
  }

  _birds(cycle) {
    // one flight a year, inside a hold of spring, summer or autumn
    const r = mulberry(9001 + cycle * 7919);
    const holds = [];
    let t0 = 0;
    for (const seg of this._script) { if (seg.s && seg.s !== 'winter') holds.push([t0, seg.d]); t0 += seg.d; }
    const h = holds[Math.floor(r() * holds.length)];
    const start = h[0] + r() * Math.max(0, h[1] - 3) - 2;
    this._u.uBird.value.set(start, r() < 0.5 ? 1 : -1, 0.12 + 0.18 * r(), 1);
  }

  update(deltaTime, profile) {
    if (this._unavailable) return;
    const paused = profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (paused && this._y) return;                       // the frame holds, never black
    if (safeZoneUniforms.uAspect.value !== this._mapAspect) this._updateMap();
    this._time = (this._time + dt) % TIME_WRAP;
    this._clock.update(this._forced != null ? 0 : dt);
    const phase = this._forced != null ? this._forced : this._clock.phase;
    const t = phase * YEAR_S;
    const cyc = this._forced != null ? 0 : this._clock.cycle;
    if (cyc !== this._cycleSeen) { this._cycleSeen = cyc; this._birds(cyc); }
    // the wind: adapt's intensity smoothed over 8 bars (R9: no impulse)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 80;
    const tau = 8 * 4 * 60 / bpm / 2;
    this._wind += (a.intensity - this._wind) * (1 - Math.exp(-dt / tau));
    const wind = clamp01(this._wind);

    const y = yearAt(this._script, t), u = this._u, P = this._A.photos;
    u.uA.value = P[y.a]; u.uB.value = P[y.b];
    if (this._debugMap >= 0) { u.uT.value = this._A.trans[this._debugMap]; u.uTT.value = 2; }
    else if (y.k >= 0) { u.uT.value = this._A.trans[y.k]; u.uTT.value = -0.08 + y.p * 1.16; }
    else u.uTT.value = 2;
    u.uFlowers.value = y.k === 3 && y.b === 'spring' ? 1 : 0;
    u.uIce.value.set(y.a === 'winter' ? 1 : 0, y.b === 'winter' ? 1 : 0);
    const w = y.w;
    u.uSeason.value.set(w.spring, w.summer, w.autumn, w.winter);
    const gr = [0, 0, 0];
    for (const s of NAMES) for (let c = 0; c < 3; c++) gr[c] += w[s] * GRADE[s][c];
    u.uGrade.value.set(gr[0], gr[1], gr[2]);
    // comfort (R10, calm): the photo's mean linear luminance under 0.25
    const ml = this._A.manifest.meanLin || {};
    let mean = 0;
    for (const s of NAMES) mean += w[s] * (ml[s] || 0.35);
    u.uGain.value = Math.min(1, 0.25 / Math.max(mean * (0.2126 * gr[0] + 0.7152 * gr[1] + 0.0722 * gr[2]), 1e-3));
    u.uTime.value = this._time; u.uWind.value = wind; u.uCycleT.value = t;
    const more = 0.55 + 0.45 * wind;
    // snow: from the second half of autumn -> winter to the first half of the melt
    let snow = w.winter;
    if (y.k === 2) snow = smooth(0.35, 0.8, y.p);
    if (y.k === 3) snow = 1 - smooth(0.1, 0.55, y.p);
    u.uDens.value.set(w.spring * more, w.summer * more, snow * more, more);
    // the leaves' clock: the autumn -> winter transition (map 2), and the
    // autumn hold before it when there is one
    const k2 = segStart(this._script, (s) => s.k === 2);
    const hold = this._script.find((s) => s.s === 'autumn');
    u.uLeafT.value.set(k2, this._script.find((s) => s.k === 2).d, hold ? hold.d : 0);
    this._y = y; this._phase = phase;
  }

  _frame(renderer) {
    const cur = renderer.getRenderTarget();
    if (cur) this._u.uView.value.set(cur.width, cur.height);
    else { renderer.getDrawingBufferSize(_size); this._u.uView.value.copy(_size); }
  }

  getProbe() {
    if (this._unavailable) return { unavailable: true };
    const y = this._y || {};
    return {
      phase: +(this._phase || 0).toFixed(4), seg: y.seg ? (y.seg.s || y.a + '>' + y.b) : null, p: +(y.p || 0).toFixed(3),
      wind: +this._wind.toFixed(3), gain: +this._u.uGain.value.toFixed(3), particles: this._count,
      map: this._u.uMap.value.toArray().map((v) => +v.toFixed(4)), seasons: this._A.seasons.length
    };
  }

  dispose() {
    for (const m of [this._quad, this._parts]) if (m) {
      m.onBeforeRender = null;
      if (this._scene) this._scene.remove(m);
      m.geometry.dispose(); m.material.dispose();
    }
    if (this._noise) this._noise.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
